import React, { useState, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { 
  FiUser, FiMail, FiPhone, FiHome, 
  FiCalendar, FiMapPin, FiCheckCircle, 
  FiLoader, FiBriefcase, FiUsers,
  FiEye, FiFile, FiShoppingCart, FiArrowLeft
} from 'react-icons/fi';

const ClientManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [products, setProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  const [selectedClient, setSelectedClient] = useState(null);
  const [clientProofs, setClientProofs] = useState([]);
  const [isLoadingProofs, setIsLoadingProofs] = useState(false);
  const [isProofModalOpen, setIsProofModalOpen] = useState(false);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    nextOfKinFullName: '',
    nextOfKinRelationship: '',
    nextOfKinEmail: '',
    nextOfKinPhone: '',
    occupation: '',
    officeAddress: ''
  });

  const [paymentFormData, setPaymentFormData] = useState({
    propertyBought: '',
    propertyPrice: '',
    noOfUnits: '1',
    imageUrl: '',
    clientId: '',
    clientName: '',
    clientEmail: '',
    clientPhone: ''
  });

 const [prooffile, setProofFile] = useState(null);
  const [errors, setErrors] = useState({});

  // Get user data from localStorage
  const data = JSON.parse(localStorage.getItem("authUser"));
  const userId = data?._id;

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoadingProducts(true);
      try {
        const response = await axios.get('https://portal-sanieldanproperties-api.onrender.com/admin/api/products');
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
        toast.error('Error loading properties');
      } finally {
        setIsLoadingProducts(false);
      }
    };
    fetchProducts();
  }, []);

  // Fetch client proofs
  const fetchClientProofs = async (clientId) => {
    setIsLoadingProofs(true);
    try {
      const response = await fetch(`https://portal-sanieldanproperties-api.onrender.com/dashboard/client-proofs/${clientId}`);
      const data = await response.json();
      if (response.ok) {
        setClientProofs(data.proofs);
      } else {
        toast.error(data.message || 'Failed to fetch client proofs');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setIsLoadingProofs(false);
    }
  };

  // Fetch clients
  useEffect(() => {
    if (activeTab === 0 && userId) {
      fetchClients();
    }
  }, [activeTab, userId]);

  const fetchClients = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`https://portal-sanieldanproperties-api.onrender.com/dashboard/clients?realtorId=${userId}`);
      const data = await response.json();
      if (response.ok) {
        setClients(data.clients);
      } else {
        toast.error(data.message || 'Failed to fetch clients');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handlePaymentInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentFormData({
      ...paymentFormData,
      [name]: value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'giweexpv');
    setProofFile(formData);
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (!formData.gender) newErrors.gender = 'Gender is required';
    if (!formData.address) newErrors.address = 'Address is required';
    if (!formData.city) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (!formData.nextOfKinFullName) newErrors.nextOfKinFullName = 'Next of kin full name is required';
    if (!formData.nextOfKinRelationship) newErrors.nextOfKinRelationship = 'Relationship is required';
    if (!formData.nextOfKinPhone) newErrors.nextOfKinPhone = 'Next of kin phone is required';
    if (!formData.occupation) newErrors.occupation = 'Occupation is required';
    if (!formData.officeAddress) newErrors.officeAddress = 'Office address is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://portal-sanieldanproperties-api.onrender.com/dashboard/add-client', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          realtorId: userId
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Client added successfully!');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          dateOfBirth: '',
          gender: '',
          address: '',
          city: '',
          state: '',
          country: '',
          zipCode: '',
          nextOfKinFullName: '',
          nextOfKinRelationship: '',
          nextOfKinEmail: '',
          nextOfKinPhone: '',
          occupation: '',
          officeAddress: ''
        });
        setActiveTab(0);
        fetchClients();
      } else {
        toast.error(data.message || 'Failed to add client');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Payment proof submission
  const handleUploadAndSendEmail = async () => {
    setIsLoading(true);
    try {
      let imageUrl;
      if (prooffile) {
        const response = await axios.post(
          'https://api.cloudinary.com/v1_1/dwpoik1jm/image/upload', 
          prooffile
        );
        imageUrl = response.data.secure_url;
      }
  
      const payload = {
        propertyBought: paymentFormData.propertyBought,
        propertyPrice: paymentFormData.propertyPrice,
        noOfUnits: paymentFormData.noOfUnits,
        imageUrl: imageUrl,
        user_id: data._id,
        clientId: paymentFormData.clientId,
        clientName: paymentFormData.clientName,
        clientEmail: paymentFormData.clientEmail,
        clientPhone: paymentFormData.clientPhone
      };
  
      const response = await axios.post(
        'https://portal-sanieldanproperties-api.onrender.com/dashboard/sendproof',
        payload
      );
  
      toast.success(response.data.message);
      setIsPurchaseModalOpen(false);
      setPaymentFormData({ 
        propertyBought: "", 
        propertyPrice: "", 
        noOfUnits: "1",
        clientId: "",
        clientName: "",
        clientEmail: "",
        clientPhone: ""
      });
      setShowPaymentForm(false);
      setSelectedProduct(null);
      setProofFile(null);
    } catch (error) {
      console.error('Error uploading file and sending email:', error);
      toast.error(error.response?.data?.message || 'Error uploading payment proof');
    } finally {
      setIsLoading(false);
    }
  };

  // Modal handlers
  const openPurchaseModal = (client) => {
    setSelectedClient(client);
    setPaymentFormData({
      ...paymentFormData,
      clientId: client._id,
      clientName: `${client.firstName} ${client.lastName}`,
      clientEmail: client.email,
      clientPhone: client.phone
    });
    setIsPurchaseModalOpen(true);
  };

  const selectProduct = (product) => {
    setSelectedProduct(product);
    setPaymentFormData({
      ...paymentFormData,
      propertyBought: product.producttitle,
      propertyPrice: product.productprice
    });
    setShowPaymentForm(true);
  };

  // Add Client Form
  const renderAddClientForm = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">Add New Client</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {/* Personal Information */}
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
              First Name <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiUser className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                name="firstName"
                id="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={`block w-full pl-10 pr-3 py-2 border ${errors.firstName ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="John"
              />
            </div>
            {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
          </div>
          
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
              Last Name <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiUser className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                name="lastName"
                id="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={`block w-full pl-10 pr-3 py-2 border ${errors.lastName ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="Doe"
              />
            </div>
            {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiMail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`block w-full pl-10 pr-3 py-2 border ${errors.email ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="john@example.com"
              />
            </div>
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
          </div>
          
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
              Phone <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-12 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">+234</span>
              </div>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiPhone className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="tel"
                name="phone"
                id="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`block w-full pl-20 pr-3 py-2 border ${errors.phone ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="8012345678"
              />
            </div>
            {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
          </div>
          
          <div>
            <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">
              Date of Birth <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiCalendar className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="date"
                name="dateOfBirth"
                id="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                className={`block w-full pl-10 pr-3 py-2 border ${errors.dateOfBirth ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              />
            </div>
            {errors.dateOfBirth && <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>}
          </div>
          
          <div>
            <label htmlFor="gender" className="block text-sm font-medium text-gray-700">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              name="gender"
              id="gender"
              value={formData.gender}
              onChange={handleInputChange}
              className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border ${errors.gender ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md`}
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
            {errors.gender && <p className="mt-1 text-sm text-red-600">{errors.gender}</p>}
          </div>
          
          {/* Address Information */}
          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-700">
              Address <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiHome className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                name="address"
                id="address"
                value={formData.address}
                onChange={handleInputChange}
                className={`block w-full pl-10 pr-3 py-2 border ${errors.address ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="123 Main St"
              />
            </div>
            {errors.address && <p className="mt-1 text-sm text-red-600">{errors.address}</p>}
          </div>
          
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700">
              City <span className="text-red-500">*</span>
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="city"
                id="city"
                value={formData.city}
                onChange={handleInputChange}
                className={`block w-full pl-3 pr-3 py-2 border ${errors.city ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="Lagos"
              />
            </div>
            {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
          </div>
          
          <div>
            <label htmlFor="state" className="block text-sm font-medium text-gray-700">
              State <span className="text-red-500">*</span>
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="state"
                id="state"
                value={formData.state}
                onChange={handleInputChange}
                className={`block w-full pl-3 pr-3 py-2 border ${errors.state ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="Lagos"
              />
            </div>
            {errors.state && <p className="mt-1 text-sm text-red-600">{errors.state}</p>}
          </div>
          
          <div>
            <label htmlFor="country" className="block text-sm font-medium text-gray-700">
              Country <span className="text-red-500">*</span>
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="country"
                id="country"
                value={formData.country}
                onChange={handleInputChange}
                className={`block w-full pl-3 pr-3 py-2 border ${errors.country ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="Nigeria"
              />
            </div>
            {errors.country && <p className="mt-1 text-sm text-red-600">{errors.country}</p>}
          </div>
          
          <div>
            <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700">
              Zip Code
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="zipCode"
                id="zipCode"
                value={formData.zipCode}
                onChange={handleInputChange}
                className="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="100001"
              />
            </div>
          </div>
        </div>
        
        {/* Next of Kin Section */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <FiUsers className="mr-2" /> Next of Kin Details
          </h3>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="nextOfKinFullName" className="block text-sm font-medium text-gray-700">
                Full Name <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="nextOfKinFullName"
                  id="nextOfKinFullName"
                  value={formData.nextOfKinFullName}
                  onChange={handleInputChange}
                  className={`block w-full pl-3 pr-3 py-2 border ${errors.nextOfKinFullName ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                  placeholder="Jane Smith"
                />
              </div>
              {errors.nextOfKinFullName && <p className="mt-1 text-sm text-red-600">{errors.nextOfKinFullName}</p>}
            </div>
            
            <div>
              <label htmlFor="nextOfKinRelationship" className="block text-sm font-medium text-gray-700">
                Relationship <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="nextOfKinRelationship"
                  id="nextOfKinRelationship"
                  value={formData.nextOfKinRelationship}
                  onChange={handleInputChange}
                  className={`block w-full pl-3 pr-3 py-2 border ${errors.nextOfKinRelationship ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                  placeholder="Spouse, Parent, Sibling, etc."
                />
              </div>
              {errors.nextOfKinRelationship && <p className="mt-1 text-sm text-red-600">{errors.nextOfKinRelationship}</p>}
            </div>
            
            <div>
              <label htmlFor="nextOfKinEmail" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiMail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  name="nextOfKinEmail"
                  id="nextOfKinEmail"
                  value={formData.nextOfKinEmail}
                  onChange={handleInputChange}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="jane@example.com"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="nextOfKinPhone" className="block text-sm font-medium text-gray-700">
                Phone <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-12 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">+234</span>
                </div>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiPhone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  name="nextOfKinPhone"
                  id="nextOfKinPhone"
                  value={formData.nextOfKinPhone}
                  onChange={handleInputChange}
                  className={`block w-full pl-20 pr-3 py-2 border ${errors.nextOfKinPhone ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                  placeholder="8012345678"
                />
              </div>
              {errors.nextOfKinPhone && <p className="mt-1 text-sm text-red-600">{errors.nextOfKinPhone}</p>}
            </div>
          </div>
        </div>
        
        {/* Work & Referral Section */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <FiBriefcase className="mr-2" /> Work & Referral Details
          </h3>
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label htmlFor="occupation" className="block text-sm font-medium text-gray-700">
                Occupation <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="occupation"
                  id="occupation"
                  value={formData.occupation}
                  onChange={handleInputChange}
                  className={`block w-full pl-3 pr-3 py-2 border ${errors.occupation ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                  placeholder="Software Engineer"
                />
              </div>
              {errors.occupation && <p className="mt-1 text-sm text-red-600">{errors.occupation}</p>}
            </div>
            
            <div>
              <label htmlFor="officeAddress" className="block text-sm font-medium text-gray-700">
                Office Address <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <textarea
                  name="officeAddress"
                  id="officeAddress"
                  rows={2}
                  value={formData.officeAddress}
                  onChange={handleInputChange}
                  className={`block w-full pl-3 pr-3 py-2 border ${errors.officeAddress ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                  placeholder="123 Business Ave, Lagos Island"
                />
              </div>
              {errors.officeAddress && <p className="mt-1 text-sm text-red-600">{errors.officeAddress}</p>}
            </div>
          </div>
        </div>
        
        <div className="flex justify-end pt-6">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-70"
          >
            {isSubmitting ? (
              <>
                <FiLoader className="animate-spin mr-2" />
                Processing...
              </>
            ) : (
              <>
                <FiCheckCircle className="mr-2" />
                Add Client
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  // View Proofs Modal
  const renderProofModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              Payment Proofs for {selectedClient?.firstName} {selectedClient?.lastName}
            </h2>
            <button
              onClick={() => setIsProofModalOpen(false)}
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {isLoadingProofs ? (
            <div className="flex justify-center items-center h-64">
              <FiLoader className="animate-spin text-blue-500 text-4xl" />
            </div>
          ) : clientProofs.length === 0 ? (
            <div className="text-center py-12">
              <FiFile className="mx-auto text-gray-400 text-5xl mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No proofs found</h3>
              <p className="mt-1 text-sm text-gray-500">This client has no payment proofs yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {clientProofs.map((proof) => (
                <div key={proof._id} className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h3 className="font-medium text-gray-900">{proof.propertyBought}</h3>
                      <p className="text-sm text-gray-600">
                        Amount: ₦{proof.propertyPrice}
                      </p>
                      <p className="text-sm text-gray-600">
                        Units: {proof.noOfUnits}
                      </p>
                      <p className="text-sm text-gray-600">
                        Status: <span className={`font-medium ${
                          proof.status === 'verified' ? 'text-green-600' : 
                          proof.status === 'rejected' ? 'text-red-600' : 'text-yellow-600'
                        }`}>
                          {proof.status}
                        </span>
                      </p>
                      {proof.rejectionReason && (
                        <p className="text-sm text-red-600">
                          Reason: {proof.rejectionReason}
                        </p>
                      )}
                    </div>
                    <div>
                      <img 
                        src={proof.imageUrl} 
                        alt="Payment proof" 
                        className="w-full h-32 object-contain rounded border"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Purchase Property Modal
  const renderPurchaseModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              {showPaymentForm ? 'Upload Payment Receipt' : 'Select Property'}
            </h2>
            <button
              onClick={() => {
                setIsPurchaseModalOpen(false);
                setShowPaymentForm(false);
                setSelectedProduct(null);
              }}
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {showPaymentForm ? (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-md">
                <h3 className="font-medium text-gray-900">Selected Property</h3>
                <p className="text-gray-600">{selectedProduct?.producttitle}</p>
                <p className="text-gray-600">Price: ₦{selectedProduct?.productprice}</p>
                <p className="text-gray-600">Location: {selectedProduct?.productlocation}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Receipt Image <span className="text-red-500">*</span></label>
                <input
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
                  type="file"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property Bought <span className="text-red-500">*</span></label>
                <input
                  name="propertyBought"
                  placeholder="Property Bought"
                  value={paymentFormData.propertyBought}
                  onChange={handlePaymentInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount Paid <span className="text-red-500">*</span></label>
                <input
                  name="propertyPrice"
                  placeholder="Amount Paid"
                  value={paymentFormData.propertyPrice}
                  onChange={handlePaymentInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Units <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  name="noOfUnits"
                  placeholder="Number of Units"
                  value={paymentFormData.noOfUnits}
                  onChange={handlePaymentInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  min="1"
                  required
                />
              </div>

              <div className="mt-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-700 mb-1">Selected Client:</h4>
                <p className="text-sm text-gray-600">
                  {paymentFormData.clientName} ({paymentFormData.clientEmail})
                </p>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowPaymentForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center"
                >
                  <FiArrowLeft className="mr-1" /> Back
                </button>
                <button
                  onClick={handleUploadAndSendEmail}
                  disabled={isLoading}
                  className={`px-4 py-2 text-white rounded-md flex items-center ${
                    isLoading ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {isLoading ? (
                    <>
                      <FiLoader className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    'Upload and Send'
                  )}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-700 mb-1">Selected Client:</h4>
                <p className="text-sm text-gray-600">
                  {paymentFormData.clientName} ({paymentFormData.clientEmail})
                </p>
              </div>

              {isLoadingProducts ? (
                <div className="flex justify-center items-center h-64">
                  <FiLoader className="animate-spin text-blue-500 text-4xl" />
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-12">
                  <FiHome className="mx-auto text-gray-400 text-5xl mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">No properties available</h3>
                  <p className="mt-1 text-sm text-gray-500">Please check back later.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {products.map((product) => (
                    <div key={product._id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                      <img 
                        src={product.productimage} 
                        alt={product.producttitle} 
                        className="w-full h-48 object-cover"
                      />
                      <div className="p-4">
                        <h3 className="font-medium text-lg text-gray-900">{product.producttitle}</h3>
                        <p className="text-gray-600 mt-1">₦{product.productprice}</p>
                        <p className="text-gray-500 text-sm mt-1">{product.productlocation}</p>
                        <p className="text-gray-500 text-sm mt-1">Units left: {product.productunitsleft}</p>
                        <button
                          onClick={() => selectProduct(product)}
                          className="mt-3 w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md"
                        >
                          Select Property
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Client Management</h1>
          
          <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
            <Tab.List className="flex space-x-1 rounded-xl bg-blue-900/20 p-1">
              <Tab
                className={({ selected }) =>
                  `w-full py-2.5 text-sm font-medium leading-5 rounded-lg transition-all duration-300 ${
                    selected
                      ? 'bg-white text-blue-700 shadow'
                      : 'text-blue-100 hover:bg-white/[0.12] hover:text-white'
                  }`
                }
              >
                View Clients
              </Tab>
              <Tab
                className={({ selected }) =>
                  `w-full py-2.5 text-sm font-medium leading-5 rounded-lg transition-all duration-300 ${
                    selected
                      ? 'bg-white text-blue-700 shadow'
                      : 'text-blue-100 hover:bg-white/[0.12] hover:text-white'
                  }`
                }
              >
                Add Client
              </Tab>
            </Tab.List>
            <Tab.Panels className="mt-4">
              <Tab.Panel>
                {isLoading ? (
                  <div className="flex justify-center items-center h-64">
                    <FiLoader className="animate-spin text-blue-500 text-4xl" />
                  </div>
                ) : clients.length === 0 ? (
                  <div className="text-center py-12">
                    <FiUser className="mx-auto text-gray-400 text-5xl mb-4" />
                    <h3 className="text-lg font-medium text-gray-900">No clients yet</h3>
                    <p className="mt-1 text-sm text-gray-500">Get started by adding your first client.</p>
                    <button
                      onClick={() => setActiveTab(1)}
                      className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Add Client
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contact
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Location
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Occupation
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {clients.map((client) => (
                          <tr key={client._id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                  <FiUser className="text-blue-600" />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{client.firstName} {client.lastName}</div>
                                  <div className="text-sm text-gray-500">@{client.username}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{client.email}</div>
                              <div className="text-sm text-gray-500">{client.phone}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{client.city}, {client.state}</div>
                              <div className="text-sm text-gray-500">{client.country}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {client.occupation}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap space-x-2">
                              <button
                                onClick={() => openPurchaseModal(client)}
                                className="text-green-600 hover:text-green-800 text-sm flex items-center"
                              >
                                <FiShoppingCart className="mr-1" /> Purchase Property
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedClient(client);
                                  fetchClientProofs(client._id);
                                  setIsProofModalOpen(true);
                                }}
                                className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                              >
                                <FiEye className="mr-1" /> View Proofs
                              </button>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Active
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Tab.Panel>
              <Tab.Panel>
                {renderAddClientForm()}
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>

      {isProofModalOpen && renderProofModal()}
      {isPurchaseModalOpen && renderPurchaseModal()}
    </div>
  );
};

export default ClientManagement;